import { Component, Inject, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { CommonModule } from '@angular/common';
import { MatSpinner } from '@angular/material/progress-spinner';
import * as pdfjsLib from 'pdfjs-dist';
import { HttpClient } from '@angular/common/http';
import { LocalForageUtilities } from "../../../utilities/browser/localForage.utilities";
import {lastValueFrom} from "rxjs";

@Component({
  selector: 'app-modal-pdf',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule, NgxExtendedPdfViewerModule, CommonModule, MatSpinner],
  templateUrl: './modal-pdf.component.html',
  styleUrls: ['./modal-pdf.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class ModalPdfComponent {
  isLoading: boolean = true;
  page: number = 1;
  pageLabels: string[] = [];
  pdf: Blob;

  constructor(
    private dialogRef: MatDialogRef<ModalPdfComponent>,
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private localForage: LocalForageUtilities,
    @Inject(MAT_DIALOG_DATA) public data: { pdfUrl: string; title: string; page?: number }
  ) {
    this.pdf = new Blob([data.pdfUrl], { type: 'application/pdf' });
    this.getPdfBlob();
  }

  async getPdfBlob(): Promise<void> {
    try {
      const cachedPdf = await this.localForage.get<any>(this.getPdfFilenameFromUrl(this.data.pdfUrl));

      if (cachedPdf) {
        this.pdf = cachedPdf;
      } else {
        try {
          // Check file size first using HEAD request
          const headResponse = await lastValueFrom(
            this.http.head(this.data.pdfUrl, {observe: 'response'})
          );

          this.pdf = await lastValueFrom(this.http.get(this.data.pdfUrl, {responseType: 'blob'}));

          const maxSafeSizeInMB = 10;
          if (this.pdf && this.pdf.size / (1024 * 1024) < maxSafeSizeInMB) {
            await this.localForage.set(this.getPdfFilenameFromUrl(this.data.pdfUrl), this.pdf, 1000 * 60 * 60 * 4); // 4 hours
          }
        } catch (error) {
          console.error('Error fetching PDF:', error);
          return;
        }
      }
      if (this.data.page) {
        this.loadPdfLabels();
      }
      this.isLoading = false;
    } catch (error) {
      console.error('Error in getPdfBlob:', error);
    }
  }

  async loadPdfLabels(): Promise<void> {
    try {

      const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(await this.pdf.arrayBuffer()) });
      const pdf = await loadingTask.promise;

      // Get all page labels
      const labels = await pdf.getPageLabels();
      this.pageLabels = labels || [];

      if (this.data.page !== undefined) {
        const stringPage = this.data.page.toString();
        const labelIndex = this.pageLabels.findIndex(label => label === stringPage);

        if (labelIndex >= 0) {
          this.page = labelIndex + 1;
        } else {
          const pageNum = parseInt(stringPage, 10);
          if (!isNaN(pageNum) && pageNum > 0 && pageNum <= pdf.numPages) {
            this.page = pageNum;
          } else {
            console.warn(`Page label "${stringPage}" not found, defaulting to page 1`);
            this.page = 1;
          }
        }
      }

      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading PDF or page labels:', error);
    }
  }

  onPdfLoaded(): void {
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  closeModal(): void {
    this.dialogRef.close();
  }

  openPDF(): void {
    const pageParam = this.data.page ? `#page=${this.data.page}` : '';
    const url = this.data.pdfUrl + pageParam;
    window.open(url, '_blank');
  }

  private getPdfFilenameFromUrl(url: string): string {
    return new URL(url).pathname;
  }

}
